/**
 * MarketSnapshotEditModal Component
 *
 * Add/Edit modal for market snapshot entries in the pre-trading workflow.
 * Supports dual mode (add vs edit) with instrument and timeframe selection.
 *
 * Add mode: Creates new MarketStateDraft with fresh fieldConfigSnapshot from current settings
 * Edit mode: Patches only instrumentSnapshot/timeframe, preserving fieldConfigSnapshot and snapshots
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from '@enfomentis/ui';
import { InstrumentDropdown } from '@/components/shared/InstrumentExplorer';
import { TimeframeDropdown } from '@/components/shared/TimeframeDropdown';

// 3. Services
import { instrumentService } from '@/services/InstrumentService';
import { marketSnapshotSettingsService } from '@/services/MarketSnapshotSettingsService';

// 4. Utils
import { generateUUID } from '@/utils';
import {
  buildInstrumentSnapshot,
  buildFieldConfigSnapshot,
} from '../utils/sessionDraftUtils';

// 5. Types
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';
import type { ImageReference } from '@/types/schema/StoredImage';
import type { MarketSnapshotSettings } from '@/types/schema/MarketSnapshotSettings';

// 6. Local components
import {
  SnapshotFieldsForm,
  type SnapshotFieldConfig,
  type SnapshotFieldValue,
} from './SnapshotFieldsForm';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for MarketSnapshotEditModal
 */
export interface MarketSnapshotEditModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Draft to edit (undefined for add mode) */
  editingDraft: MarketStateDraft | undefined;
  /** All existing drafts for uniqueness validation */
  existingDrafts: MarketStateDraft[];
  /** Callback when save completes with the new/updated draft */
  onSave: (draft: MarketStateDraft) => void;
}

/**
 * Form validation errors
 */
interface FormErrors {
  instrument?: string;
  timeframe?: string;
  uniqueness?: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Validate URL field value
 * Empty values are valid (field is optional), but non-empty must start with https://
 */
const validateUrl = (value: string): string | undefined => {
  if (!value || value.trim() === '') return undefined;
  if (!value.startsWith('https://')) return 'URL must start with https://';
  return undefined;
};

/**
 * Validate all URL fields in a values object
 * Returns validation errors object (empty if all valid)
 */
const validateUrlFields = (
  values: Partial<Record<string, SnapshotFieldValue>>,
  fields: SnapshotFieldConfig[]
): Partial<Record<string, string>> => {
  const errors: Partial<Record<string, string>> = {};

  for (const field of fields) {
    if (field.type === 'url') {
      const value = values[field.fieldId];
      if (typeof value === 'string') {
        const error = validateUrl(value);
        if (error) {
          errors[field.fieldId] = error;
        }
      }
    }
  }

  return errors;
};

/**
 * Check if a value is an ImageReference
 */
const isImageReference = (value: SnapshotFieldValue): value is ImageReference => {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'type' in value &&
    value.type === 'image'
  );
};

/**
 * Clean form values according to empty value semantics:
 * - Omit: null, undefined, empty string '', whitespace-only strings
 * - Keep: false, 0, valid values
 * - Trim text/url string values
 */
const cleanFormValues = (
  values: Partial<Record<string, SnapshotFieldValue>>,
  fields: SnapshotFieldConfig[]
): Record<string, string | number | boolean | ImageReference | null> => {
  const result: Record<string, string | number | boolean | ImageReference | null> = {};

  for (const field of fields) {
    const value = values[field.fieldId];

    // Handle text and url fields - trim and check for empty
    if (field.type === 'text' || field.type === 'url') {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '') {
          result[field.fieldId] = trimmed;
        }
      }
      continue;
    }

    // Handle number fields - keep 0, omit undefined/null
    if (field.type === 'number') {
      if (typeof value === 'number') {
        result[field.fieldId] = value;
      }
      continue;
    }

    // Handle checkbox - keep false, omit undefined
    if (field.type === 'checkbox') {
      if (typeof value === 'boolean') {
        result[field.fieldId] = value;
      }
      continue;
    }

    // Handle dropdown - keep non-empty string
    if (field.type === 'dropdown') {
      if (typeof value === 'string' && value !== '') {
        result[field.fieldId] = value;
      }
      continue;
    }

    // Handle image - preserve existing ImageReference
    if (field.type === 'image') {
      if (isImageReference(value)) {
        result[field.fieldId] = value;
      }
      continue;
    }
  }

  return result;
};

/**
 * Serialize form values for isDirty comparison
 * Uses cleanFormValues for normalization, then sorts keys for stable comparison
 */
const serializeFormValues = (
  values: Partial<Record<string, SnapshotFieldValue>>,
  fields: SnapshotFieldConfig[]
): string => {
  const cleaned = cleanFormValues(values, fields);
  const sortedKeys = Object.keys(cleaned).sort();
  const ordered: Record<string, unknown> = {};
  for (const key of sortedKeys) {
    ordered[key] = cleaned[key];
  }
  return JSON.stringify(ordered);
};

// =============================================================================
// Component
// =============================================================================

/**
 * MarketSnapshotEditModal Component
 */
export const MarketSnapshotEditModal: React.FC<MarketSnapshotEditModalProps> = ({
  isOpen,
  onClose,
  editingDraft,
  existingDrafts,
  onSave,
}) => {
  // Portal container ref for dropdown popovers (fixes aria-hidden warning)
  const portalContainerRef = useRef<HTMLDivElement>(null);

  // Cache settings for add mode to avoid re-fetch drift during save
  const addModeSettingsRef = useRef<MarketSnapshotSettings | null>(null);

  // Track open state for async cancellation (component stays mounted, modal opens/closes)
  const isOpenRef = useRef(false);

  // isDirty tracking refs - capture initial state on modal open
  const initialInstrumentIdRef = useRef<string | null>(null);
  const initialTimeframeRef = useRef<string | null>(null);
  const initialFormValuesSnapshotRef = useRef<string>('');

  // Form state - instrument/timeframe selection
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snapshot field form state
  const [formValues, setFormValues] = useState<Partial<Record<string, SnapshotFieldValue>>>({});
  const [fieldValidationErrors, setFieldValidationErrors] = useState<Partial<Record<string, string>>>({});
  const [previewFields, setPreviewFields] = useState<SnapshotFieldConfig[]>([]);
  const [settingsLoadError, setSettingsLoadError] = useState(false);

  // isDirty tracking state - prevents first-render flicker
  const [isDirtyReady, setIsDirtyReady] = useState(false);

  // Determine mode
  const isEditMode = !!editingDraft;

  // =============================================================================
  // Effects
  // =============================================================================

  // Sync isOpenRef with isOpen prop for async cancellation
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Populate form when editingDraft changes (edit mode)
  useEffect(() => {
    if (editingDraft) {
      const initialInstrumentId = editingDraft.instrumentSnapshot?.instrumentId ?? null;
      const initialTimeframe = editingDraft.timeframe;
      setSelectedInstrumentId(initialInstrumentId);
      setSelectedTimeframe(initialTimeframe);
      // Initialize form values from existing 'pre' snapshot
      const preSnapshot = editingDraft.snapshots.find((s) => s.type === 'pre');
      const initialValues = preSnapshot?.data ?? {};
      setFormValues(initialValues);
      // Validate URL fields on load to catch pre-existing invalid values
      setFieldValidationErrors(
        validateUrlFields(initialValues, editingDraft.fieldConfigSnapshot)
      );

      // Capture baseline for isDirty tracking
      initialInstrumentIdRef.current = initialInstrumentId;
      initialTimeframeRef.current = initialTimeframe;
      initialFormValuesSnapshotRef.current = serializeFormValues(
        initialValues,
        editingDraft.fieldConfigSnapshot ?? []
      );
      setIsDirtyReady(true);
    }
  }, [editingDraft]);

  /**
   * Fetch snapshot settings for add mode
   * Extracted as helper for reuse by retry handler
   */
  const fetchSnapshotSettings = useCallback(async () => {
    try {
      setSettingsLoadError(false);
      const settings = await marketSnapshotSettingsService.getCurrentSettings();

      // Guard: modal may have closed during async fetch
      if (!isOpenRef.current) return;

      // Cache settings for reuse during save
      addModeSettingsRef.current = settings;

      // Build field config from settings
      const snapshotFields = settings?.snapshotFields ?? [];
      const fieldConfig = buildFieldConfigSnapshot(snapshotFields);
      setPreviewFields(fieldConfig);

      // Capture baseline for isDirty tracking (add mode starts empty)
      initialInstrumentIdRef.current = null;
      initialTimeframeRef.current = null;
      initialFormValuesSnapshotRef.current = serializeFormValues({}, fieldConfig);
      setIsDirtyReady(true);
    } catch (error) {
      // Guard: modal may have closed during async fetch
      if (!isOpenRef.current) return;

      console.error('Failed to fetch snapshot settings:', error);
      // Set minimal valid settings so save is unblocked
      addModeSettingsRef.current = {
        id: '',
        createdAt: '',
        updatedAt: '',
        snapshotFields: [],
        defaultSnapshots: [],
      } as MarketSnapshotSettings;
      setPreviewFields([]);
      setSettingsLoadError(true);

      // Capture baseline for isDirty tracking even on error (add mode starts empty)
      initialInstrumentIdRef.current = null;
      initialTimeframeRef.current = null;
      initialFormValuesSnapshotRef.current = serializeFormValues({}, []);
      setIsDirtyReady(true);
    }
  }, []);

  /**
   * Handle retry of settings load
   */
  const handleRetrySettingsLoad = useCallback(() => {
    fetchSnapshotSettings();
  }, [fetchSnapshotSettings]);

  // Initialize form when modal opens in add mode
  useEffect(() => {
    // Only run in add mode when modal is open
    if (!isOpen || editingDraft) {
      return;
    }

    // Reset instrument/timeframe selection
    setSelectedInstrumentId(null);
    setSelectedTimeframe(null);
    setErrors({});
    setSubmitError(null);

    // Reset form values
    setFormValues({});
    setFieldValidationErrors({});
    setSettingsLoadError(false);

    // Fetch current settings for preview fields
    fetchSnapshotSettings();
  }, [isOpen, editingDraft, fetchSnapshotSettings]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setSubmitError(null);
      setIsSubmitting(false);
      setFormValues({});
      setFieldValidationErrors({});
      setPreviewFields([]);
      setSettingsLoadError(false);
      addModeSettingsRef.current = null;

      // Reset isDirty tracking to avoid stale comparisons on quick reopen
      setIsDirtyReady(false);
      initialInstrumentIdRef.current = null;
      initialTimeframeRef.current = null;
      initialFormValuesSnapshotRef.current = '';
    }
  }, [isOpen]);

  // =============================================================================
  // Validation
  // =============================================================================

  /**
   * Check if instrument+timeframe combination is unique
   */
  const checkUniqueness = useCallback(
    (instrumentId: string | null, timeframe: string | null): string | undefined => {
      if (!instrumentId || !timeframe) return undefined;

      const key = `${instrumentId}:${timeframe}`;
      const isDuplicate = existingDrafts.some((draft) => {
        // In edit mode, exclude the draft being edited
        if (isEditMode && editingDraft && draft.id === editingDraft.id) {
          return false;
        }
        const draftKey = `${draft.instrumentSnapshot?.instrumentId}:${draft.timeframe}`;
        return draftKey === key;
      });

      return isDuplicate ? 'This instrument/timeframe combination already exists' : undefined;
    },
    [existingDrafts, isEditMode, editingDraft]
  );

  /**
   * Validate form and return whether it's valid
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!selectedInstrumentId) {
      newErrors.instrument = 'Please select an instrument';
    }

    if (!selectedTimeframe) {
      newErrors.timeframe = 'Please select a timeframe';
    }

    const uniquenessError = checkUniqueness(selectedInstrumentId, selectedTimeframe);
    if (uniquenessError) {
      newErrors.uniqueness = uniquenessError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [selectedInstrumentId, selectedTimeframe, checkUniqueness]);

  // =============================================================================
  // Handlers
  // =============================================================================

  /**
   * Handle instrument selection change
   */
  const handleInstrumentChange = useCallback((instrumentId: string) => {
    setSelectedInstrumentId(instrumentId);
    // Clear instrument error and uniqueness error by setting new object without those keys
    setErrors((prev) => {
      const { instrument: _instrument, uniqueness: _uniqueness, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Handle timeframe selection change
   */
  const handleTimeframeChange = useCallback((value: string) => {
    setSelectedTimeframe(value);
    // Clear timeframe error and uniqueness error by setting new object without those keys
    setErrors((prev) => {
      const { timeframe: _timeframe, uniqueness: _uniqueness, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Handle snapshot field form values change
   * Updates values and runs URL validation
   */
  const handleFormValuesChange = useCallback(
    (newValues: Partial<Record<string, SnapshotFieldValue>>) => {
      setFormValues(newValues);

      // Run URL validation for all url fields
      const fields = isEditMode ? editingDraft?.fieldConfigSnapshot ?? [] : previewFields;
      setFieldValidationErrors(validateUrlFields(newValues, fields));
    },
    [isEditMode, editingDraft?.fieldConfigSnapshot, previewFields]
  );

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const now = new Date().toISOString();

      if (isEditMode && editingDraft) {
        // Edit mode: patch only changed fields
        let updatedDraft: MarketStateDraft = {
          ...editingDraft,
          updatedAt: now,
        };

        // Check if instrument changed
        const originalInstrumentId = editingDraft.instrumentSnapshot?.instrumentId;
        if (selectedInstrumentId && selectedInstrumentId !== originalInstrumentId) {
          const instrument = await instrumentService.get(selectedInstrumentId);
          if (!instrument || instrument.deletedAt || instrument.status !== 'active') {
            setSubmitError('Selected instrument is inactive or has been removed. Please select a different instrument.');
            return;
          }
          updatedDraft = {
            ...updatedDraft,
            instrumentSnapshot: buildInstrumentSnapshot(instrument),
          };
        }

        // Check if timeframe changed
        if (selectedTimeframe && selectedTimeframe !== editingDraft.timeframe) {
          updatedDraft = {
            ...updatedDraft,
            timeframe: selectedTimeframe,
          };
        }

        // Update 'pre' snapshot with form values
        const cleanedData = cleanFormValues(formValues, editingDraft.fieldConfigSnapshot);
        const existingPreIndex = editingDraft.snapshots.findIndex((s) => s.type === 'pre');
        const hasData = Object.keys(cleanedData).length > 0;

        let updatedSnapshots = [...editingDraft.snapshots];
        if (existingPreIndex >= 0) {
          // Update existing 'pre' snapshot - explicitly set type to preserve it
          updatedSnapshots[existingPreIndex] = {
            type: 'pre',
            data: cleanedData,
            capturedAt: now,
          };
        } else if (hasData) {
          // Create new 'pre' snapshot (only if there's data)
          updatedSnapshots = [
            { type: 'pre', capturedAt: now, data: cleanedData },
            ...updatedSnapshots,
          ];
        }

        updatedDraft = {
          ...updatedDraft,
          snapshots: updatedSnapshots,
        };

        onSave(updatedDraft);
      } else {
        // Add mode: create new draft
        if (!selectedInstrumentId || !selectedTimeframe) {
          // Should not happen due to validation, but guard anyway
          return;
        }

        const instrument = await instrumentService.get(selectedInstrumentId);
        if (!instrument || instrument.deletedAt || instrument.status !== 'active') {
          setSubmitError('Selected instrument is inactive or has been removed. Please select a different instrument.');
          return;
        }

        // Use cached settings from effect (avoids re-fetch drift)
        const settings = addModeSettingsRef.current;
        const snapshotFields = settings?.snapshotFields ?? [];
        const fieldConfig = buildFieldConfigSnapshot(snapshotFields);

        // Build 'pre' snapshot if any values present
        const cleanedData = cleanFormValues(formValues, fieldConfig);
        const hasData = Object.keys(cleanedData).length > 0;
        const snapshots: MarketStateDraft['snapshots'] = hasData
          ? [{ type: 'pre', capturedAt: now, data: cleanedData }]
          : [];

        const newDraft: MarketStateDraft = {
          id: generateUUID(),
          createdAt: now,
          updatedAt: now,
          instrumentSnapshot: buildInstrumentSnapshot(instrument),
          timeframe: selectedTimeframe,
          fieldConfigSnapshot: fieldConfig,
          snapshots,
        };

        onSave(newDraft);
      }

      onClose();
    } catch (error) {
      console.error('Failed to save market snapshot:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // =============================================================================
  // Derived State
  // =============================================================================

  // Live uniqueness check for immediate feedback
  const liveUniquenessError = checkUniqueness(selectedInstrumentId, selectedTimeframe);

  // Check if there are field validation errors
  const hasFieldValidationErrors = Object.keys(fieldValidationErrors).length > 0;

  // isDirty tracking - compare current state to initial baseline
  const isDirty = useMemo(() => {
    if (!isDirtyReady) return false;
    const fields = isEditMode ? editingDraft?.fieldConfigSnapshot ?? [] : previewFields;
    const currentSnapshot = serializeFormValues(formValues, fields);
    return (
      selectedInstrumentId !== initialInstrumentIdRef.current ||
      selectedTimeframe !== initialTimeframeRef.current ||
      currentSnapshot !== initialFormValuesSnapshotRef.current
    );
  }, [
    isDirtyReady,
    selectedInstrumentId,
    selectedTimeframe,
    formValues,
    isEditMode,
    editingDraft?.fieldConfigSnapshot,
    previewFields,
  ]);

  // Check if save should be disabled (use live uniqueness check for immediate feedback)
  // In add mode, also disable until settings have loaded (prevents saving with empty fieldConfigSnapshot)
  const isSaveDisabled =
    isSubmitting ||
    !selectedInstrumentId ||
    !selectedTimeframe ||
    !!liveUniquenessError ||
    hasFieldValidationErrors ||
    (!isEditMode && addModeSettingsRef.current === null) ||
    !isDirtyReady ||
    !isDirty;

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Edit Snapshot' : 'Add Snapshot'}</ModalHeader>

        <ModalBody className="gap-4">
          {/* Portal container for dropdown popovers */}
          <div ref={portalContainerRef}>
            {/* Instrument Selection */}
            <InstrumentDropdown
              value={selectedInstrumentId}
              onChange={handleInstrumentChange}
              label="Instrument"
              placeholder="Select instrument"
              disableAnimation
              {...(portalContainerRef.current && {
                popoverProps: { portalContainer: portalContainerRef.current },
              })}
            />
            {errors.instrument && (
              <p className="text-tiny text-danger mt-1">{errors.instrument}</p>
            )}

            {/* Timeframe Selection */}
            <div className="mt-4">
              <TimeframeDropdown
                value={selectedTimeframe}
                onChange={handleTimeframeChange}
                label="Timeframe"
                placeholder="Select timeframe"
                disableAnimation
                {...(portalContainerRef.current && {
                  popoverProps: { portalContainer: portalContainerRef.current },
                })}
              />
              {errors.timeframe && (
                <p className="text-tiny text-danger mt-1">{errors.timeframe}</p>
              )}
            </div>
          </div>

          {/* Uniqueness Error */}
          {liveUniquenessError && (
            <Alert color="warning" variant="flat">
              {liveUniquenessError}
            </Alert>
          )}

          {/* Snapshot Fields Form */}
          <SnapshotFieldsForm
            fields={isEditMode ? editingDraft.fieldConfigSnapshot : previewFields}
            values={formValues}
            onValuesChange={handleFormValuesChange}
            validationErrors={fieldValidationErrors}
            loadError={!isEditMode && settingsLoadError}
            onRetry={!isEditMode ? handleRetrySettingsLoad : undefined}
          />

          {/* Submit Error */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={isSaveDisabled}
          >
            {isEditMode ? 'Save Changes' : 'Add Snapshot'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MarketSnapshotEditModal;
